import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function PublicProofExplorer() {
  const [txHash, setTxHash] = useState('');
  const [loading, setLoading] = useState(false);
  const [proof, setProof] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);

  async function handleVerify() {
    if (!txHash.trim()) {
      toast.error('Please enter a transaction hash');
      return;
    }

    setLoading(true);
    setNotFound(false);
    setProof(null);

    try {
      const { data, error } = await supabase.functions.invoke('public-verify', {
        body: { txHash: txHash.trim() },
      });

      if (error) throw error;

      if (data.found) {
        setProof(data.proof);
        toast.success('Proof verified successfully');
      } else {
        setNotFound(true);
        toast.error('No proof found for this transaction hash');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Public Proof Verification</h1>
          <p className="text-muted-foreground">Verify on-chain compliance proofs without login</p>
        </div>

        <Card className="border-border">
          <CardHeader>
            <CardTitle>Enter Transaction Hash</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="txHash">Transaction Hash</Label>
              <div className="flex gap-2">
                <Input
                  id="txHash"
                  placeholder="0x..."
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                />
                <Button onClick={handleVerify} disabled={loading}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {notFound && (
              <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <XCircle className="h-5 w-5 text-destructive" />
                <p className="text-sm text-destructive font-medium">
                  No proof found for this transaction hash
                </p>
              </div>
            )}

            {proof && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <p className="text-sm text-primary font-medium">Proof verified successfully</p>
                </div>

                <div className="grid gap-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Company</Label>
                    <p className="font-medium">{proof.companyName}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Event Type</Label>
                    <p className="font-medium">{proof.eventType}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Blockchain</Label>
                    <p className="font-medium uppercase">{proof.chain}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">SHA-256 Hash</Label>
                    <p className="font-mono text-xs break-all">{proof.sha256}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Timestamp</Label>
                    <p className="font-medium">{new Date(proof.timestamp).toLocaleString()}</p>
                  </div>

                  {proof.verifierUrl && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(proof.verifierUrl, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View on Blockchain Explorer
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          This is a public verification page. No login required.
        </p>
      </div>
    </div>
  );
}
